import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type SuccessResponse, zodSuccessResponseSchema } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { queryKeysCompany } from './queryKeys';

type DeleteInvoiceAllowedSenderAddressVariables = {
  companyId: UUID;
  email: string;
};

/**
 * Deletes an invoice allowed sender address from a company.
 *
 * @param variables - The variables needed for the mutation.
 * @param variables.companyId - The ID of the company.
 * @param variables.email - The email address to delete.
 * @returns A promise that resolves when the invoice allowed sender address is deleted.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/delete_invoice_allowed_sender_address_vadmin_company__company_id__invoice_allowed_sender_address__email__delete
 */
// fallow-ignore-next-line unused-export
export const deleteCompanyInvoiceAllowedSenderAddress = async ({
  companyId,
  email,
}: DeleteInvoiceAllowedSenderAddressVariables) => {
  try {
    return await vestigasApi
      .delete(
        ENDPOINT.COMPANY.INVOICE_ALLOWED_SENDER_ADDRESS.DELETE(
          companyId,
          email,
        ),
        { validate: { response: zodSuccessResponseSchema } },
      )
      .json<SuccessResponse>();
  } catch (error) {
    log.error({
      message: 'Error deleting invoice allowed sender address',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * React Query mutation hook for deleting an invoice allowed sender address from a company.
 *
 * @param options - Additional options for the useMutation hook.
 * @returns The result of the useMutation hook.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/delete_invoice_allowed_sender_address_vadmin_company__company_id__invoice_allowed_sender_address__email__delete
 */
export const useMutationCompanyDeleteInvoiceAllowedSenderAddress = (
  options?: Omit<
    UseMutationOptions<
      SuccessResponse,
      Error,
      DeleteInvoiceAllowedSenderAddressVariables
    >,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation<
    SuccessResponse,
    Error,
    DeleteInvoiceAllowedSenderAddressVariables
  >({
    mutationFn: deleteCompanyInvoiceAllowedSenderAddress,
    ...options,
    onSettled(data, error, variables, onMutateResult, context) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysCompany.getInvoiceAllowedSenderAddresses(
          variables.companyId,
        ),
      });

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
  });
};
